import { isPlatformNodeJS } from '@utils/common';
import { extractFileNameFromImageSrc } from './extractFileNameFromImageSrc.js';
import { transformInputV1ToV2 } from './transformInputV1ToV2.js';
import { transformShapeV1ToLegacyCanvasShapeV2 } from './transformShapeV1ToLegacyCanvasShapeV2.js';

const transformPageV1ToV2 = async (page, index, supportedShapeColoring) => {
  let imageMetaData;
  try {
    imageMetaData = await extractMetaDataFromImage(page.background_file);
  } catch {
    let maxWidth = 1600;
    let maxHeight = 900;
    for (const input of page.inputs ?? []) {
      maxWidth = Math.max(maxWidth, input.origin.x + input.size.width + 90);
      maxHeight = Math.max(maxHeight, input.origin.y + input.size.height + 90);
    }
    imageMetaData = {
      aspectRatio: maxHeight / maxWidth,
      size: {
        width: maxWidth,
        height: maxHeight
      }
    };
  }
  const { aspectRatio, size } = imageMetaData;
  const bgItem = transformBackgroundImageIntoItem(
    index,
    page.background_file,
    page.thumb_file,
    size
  );
  const legacyCanvasShapeItems = (page.shape_drawing ?? []).map((canvasShape, index2) => ({ ...canvasShape, originalIndex: index2 })).sort((a, b) => a.weight > b.weight ? 1 : -1).map(
    (canvasShape) => transformShapeV1ToLegacyCanvasShapeV2(
      canvasShape,
      aspectRatio,
      index,
      canvasShape.originalIndex,
      supportedShapeColoring
    )
  );
  const inputItems = (page.inputs ?? []).map((input, index2) => ({ ...input, originalIndex: index2 })).sort((a, b) => a.weight > b.weight ? 1 : -1).map(
    (input) => transformInputV1ToV2(input, index, input.originalIndex, size)
  );
  return {
    id: index.toString(),
    title: page.originalPageNum != null && page.originalPageNum !== 0 ? `${page.pageTitle} (side ${page.originalPageNum.toString()})` : page.pageTitle,
    aspectRatio,
    naturalSize: size,
    items: [bgItem, ...legacyCanvasShapeItems, ...inputItems]
  };
};
function transformBackgroundImageIntoItem(pageIndex, backgroundSrc, thumbnailSrc, size) {
  return {
    kind: "image",
    id: `${pageIndex.toString()}_backgroundImage`,
    src: backgroundSrc,
    thumbnail: thumbnailSrc,
    alt: null,
    longDesc: null,
    fileName: extractFileNameFromImageSrc(backgroundSrc),
    updatedAt: Date.now(),
    position: { x: 0, y: 0 },
    size: { width: 1, height: 1 },
    naturalSize: size,
    angleInRadian: 0,
    settings: {
      cloneable: false,
      movable: false,
      showCrediting: false,
      hideDragHandle: true
    },
    crediting: {
      title: "",
      description: "",
      author: "",
      creditLine: "",
      year: ""
    }
  };
}
async function extractMetaDataFromImage(src) {
  return await new Promise((resolve, reject) => {
    if (isPlatformNodeJS()) {
      import('image-size').then(({ default: sizeOf }) => {
        fetch(src).then((req) => req.blob()).then((blob) => blob.arrayBuffer()).then((arrayBuffer) => Buffer.from(arrayBuffer)).then((buffer) => {
          const imageSize = sizeOf(buffer);
          if (imageSize.height === void 0 || imageSize.width === void 0) {
            throw new Error("Unable to load image");
          }
          return {
            aspectRatio: imageSize.height / imageSize.width,
            size: {
              height: imageSize.height,
              width: imageSize.width
            }
          };
        }).then(
          (result) => {
            resolve(result);
          },
          (err) => reject(err)
        );
      }).catch((err) => reject(err));
      return;
    }
    const img = document.createElement("img");
    img.onload = () => {
      document.body.removeChild(img);
      resolve({
        aspectRatio: img.naturalHeight / img.naturalWidth,
        size: {
          height: img.naturalHeight,
          width: img.naturalWidth
        }
      });
    };
    img.onabort = img.onerror = () => {
      document.body.removeChild(img);
      reject(new Error("Unable to load image"));
    };
    img.src = src;
    img.style.cssText = `
      position: absolute;
      top: -999px;
      left: -999px;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    `;
    document.body.appendChild(img);
  });
}

export { transformPageV1ToV2 };
//# sourceMappingURL=transformPageV1ToV2.js.map
